import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, PaginationQuerySchema } from "./common.js";
import { COMPANY_KINDS, CRAWL_VERDICTS, HARNESS_IDS, HARNESS_VERDICTS } from "../constants/index.js";

/**
 * 진실의 원천: harness/API-SPEC.md §12, harness/SECURITY.md, harness/HARNESS.md
 */

export const UserSchema = z.object({
  id: IdSchema,
  email: z.string().email(),
  name: z.string(),
  is_active: z.boolean(),
  created_at: IsoDateTimeSchema,
});
export type User = z.infer<typeof UserSchema>;

export const UserCreateRequestSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  password: z.string().min(8),
});
export type UserCreateRequest = z.infer<typeof UserCreateRequestSchema>;

export const CompanySchema = z.object({
  id: IdSchema,
  name: z.string().min(1),
  kind: z.enum(COMPANY_KINDS),
  contact: z.string().nullable(),
  memo: z.string().nullable(),
  // 별칭을 함께 실어야 대표가 중복 등록을 눈으로 막을 수 있다 (harness/API-SPEC.md §12)
  aliases: z.array(z.string()),
});
export type Company = z.infer<typeof CompanySchema>;

// 협력사는 대표가 직접 추가/수정 — 자동 생성 금지 (harness/DATA-DICTIONARY.md §1)
export const CompanyCreateRequestSchema = z.object({
  // .trim().min(1) — 공백뿐인 회사명은 이름이 아니다. 별칭과 달리 조용히 버릴 수 없다
  // (버리면 이름 없는 회사가 남는다). 프론트도 같은 스키마로 막는다.
  name: z.string().trim().min(1),
  kind: z.enum(COMPANY_KINDS),
  contact: z.string().optional(),
  memo: z.string().optional(),
  aliases: z.array(z.string().min(1)).optional(),
});
export type CompanyCreateRequest = z.infer<typeof CompanyCreateRequestSchema>;

/**
 * 수정. 보낸 필드만 바꾼다 (harness/API-SPEC.md §12 규칙 4).
 * `aliases` 를 보내면 그 배열이 별칭 전체를 대체한다 — 빼려면 빼고 보낸다.
 * `contact`/`memo` 는 null 을 보내 비울 수 있다. undefined(미전송)와 null 은 다르다.
 */
export const CompanyUpdateRequestSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    kind: z.enum(COMPANY_KINDS).optional(),
    contact: z.string().nullable().optional(),
    memo: z.string().nullable().optional(),
    aliases: z.array(z.string().min(1)).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: "바꿀 항목을 하나 이상 보내야 합니다.",
  });
export type CompanyUpdateRequest = z.infer<typeof CompanyUpdateRequestSchema>;

export const AuditLogQuerySchema = PaginationQuerySchema.extend({
  entity: z.string().optional(),
  user_id: z.coerce.number().int().positive().optional(),
  from: IsoDateTimeSchema.optional(),
  to: IsoDateTimeSchema.optional(),
  // 커서는 마지막으로 본 audit_log.id 다. 형식을 막지 않으면 깨진 커서가
  // "결과 없음"으로 조용히 내려간다 — 케이스 목록과 같은 규칙이다(반증 RT-8).
  cursor: z.string().regex(/^\d+$/, "커서 형식이 올바르지 않습니다").optional(),
});
export type AuditLogQuery = z.infer<typeof AuditLogQuerySchema>;

export const AuditLogEntrySchema = z.object({
  id: IdSchema,
  at: IsoDateTimeSchema,
  user_id: IdSchema.nullable(),
  action: z.string(),
  entity: z.string(),
  entity_id: z.string().nullable(),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;

export const AuditLogResponseSchema = z.object({
  items: z.array(AuditLogEntrySchema),
  next_cursor: z.string().nullable(),
});
export type AuditLogResponse = z.infer<typeof AuditLogResponseSchema>;

export const HarnessStatusSchema = z.object({
  harness: z.enum(HARNESS_IDS),
  verdict: z.enum(HARNESS_VERDICTS),
  run_at: IsoDateTimeSchema,
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type HarnessStatus = z.infer<typeof HarnessStatusSchema>;

/**
 * 수집 상태 (harness/API-SPEC.md §11-2, harness/CRAWL-POLICY.md §5).
 *
 * 화면이 물어보는 것은 세 가지다 — **마지막에 언제 돌았나 · 잘 됐나 · 지금 내가 뭘 해야 하나.**
 * 그 셋을 서버가 판정해서 내려준다. 화면이 시각을 빼서 스스로 계산하면, "언제부터
 * 낡은 것으로 볼 것인가" 라는 규칙이 화면과 문서 두 곳에 살게 된다.
 */
export const CrawlStatusSchema = z.object({
  last_run: z
    .object({
      id: IdSchema,
      started_at: IsoDateTimeSchema,
      finished_at: IsoDateTimeSchema.nullable(),
      verdict: z.enum(CRAWL_VERDICTS).nullable(),
      cases_found: z.number().int().nullable(),
      cases_new: z.number().int().nullable(),
      swaps_matched: z.number().int().nullable(),
      error: z.string().nullable(),
    })
    .nullable(),
  /** 마지막으로 **성공**한 수집 시각. 실패만 반복되면 이 값이 멈춰 있다 */
  last_ok_at: IsoDateTimeSchema.nullable(),
  /** 사람이 다시 로그인해야 하는가 (마지막 판정이 SESSION_EXPIRED) */
  needs_login: z.boolean(),
  /** 26시간 넘게 성공이 없다 — 하루 1회 도는 것이 두 번 걸렀다는 뜻이다 */
  stale: z.boolean(),
  /** 한 번도 돈 적이 없다. "실패" 와 다르다 — 없는 것을 실패로 칠하지 않는다 */
  never_ran: z.boolean(),
});
export type CrawlStatus = z.infer<typeof CrawlStatusSchema>;
